import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Plus } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable, DateValue, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import {
  useFolders, useLibrary, useLibraryMutations, useRetentionRules,
} from '../api'
import { LIBRARY_KIND_LABELS, LIBRARY_STATUS_LABELS } from '../types'
import type { LibraryDocumentView, LibraryKind, LibraryStatus } from '../types'

const STATUS_TONE: Record<LibraryStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
}

/** Everything the company has written down, and which version is current. */
export function DocumentLibraryPage() {
  useBreadcrumb()
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [sort, setSort] = useState<string | undefined>('title,asc')
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')
  const [folderId, setFolderId] = useState('')

  const documents = useLibrary({ page, size, sort, q: search, kind, folderId })
  const folders = useFolders()
  const rules = useRetentionRules()
  const documentTypes = useDocumentTypes()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveDocument } = useLibraryMutations()

  const [draft, setDraft] = useState<{
    folderId: string; kind: LibraryKind; title: string; summary: string; tags: string
    ownerEmployeeId: string; documentTypeId: string; reviewDueOn: string; retentionRuleId: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const columns = useMemo<ColumnDef<LibraryDocumentView, unknown>[]>(() => [
    {
      id: 'title',
      header: 'Title',
      accessorFn: (row) => row.title,
      cell: ({ row }) => (
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => { void navigate(`/library/${row.original.id}`) }}
        >
          {row.original.title}
        </button>
      ),
    },
    {
      id: 'kind',
      header: 'Kind',
      accessorFn: (row) => LIBRARY_KIND_LABELS[row.kind as LibraryKind] ?? row.kind,
    },
    { id: 'folderName', header: 'Folder', accessorFn: (row) => row.folderName ?? '—' },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={STATUS_TONE[row.original.status as LibraryStatus] ?? 'neutral'}
          label={LIBRARY_STATUS_LABELS[row.original.status as LibraryStatus] ?? row.original.status}
        />
      ),
    },
    {
      id: 'currentVersionNo',
      header: 'Version',
      accessorFn: (row) => row.currentVersionNo,
      cell: ({ row }) => row.original.currentVersionNo == null
        ? <span className="text-slate-500">None published</span>
        : <span>v{row.original.currentVersionNo} of {row.original.versionCount}</span>,
    },
    {
      id: 'reviewDueOn',
      header: 'Review due',
      accessorFn: (row) => row.reviewDueOn,
      cell: ({ row }) => row.original.reviewDueOn
        ? <DateValue value={row.original.reviewDueOn} />
        : <span className="text-slate-500" title={row.original.alertNote}>Nobody has said</span>,
    },
    { id: 'ownerName', header: 'Owner', accessorFn: (row) => row.ownerName ?? '—' },
  ], [navigate])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Document library"
        subtitle="What the company has written down, which version is current, and who owns it."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                folderId: folders.data?.[0]?.id ?? '', kind: 'POLICY', title: '', summary: '',
                tags: '', ownerEmployeeId: '', documentTypeId: '', reviewDueOn: '',
                retentionRuleId: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New entry
          </Button>
        }
      />

      <DataTable<LibraryDocumentView>
        columns={columns}
        data={documents.data}
        isLoading={documents.isPending}
        error={documents.error}
        onRetry={() => void documents.refetch()}
        page={page}
        size={size}
        sort={sort}
        search={search}
        onPageChange={setPage}
        onSizeChange={setSize}
        onSortChange={setSort}
        onSearchChange={(value) => { setSearch(value); setPage(0) }}
        emptyTitle="The library is empty"
        emptyDescription="A folder decides who can open what. Nothing here is seeded: the folders, the retention periods and the policies are all yours."
        toolbar={
          <>
            <Select
              aria-label="Kind"
              value={kind}
              onChange={(event) => { setKind(event.target.value); setPage(0) }}
            >
              <option value="">Any kind</option>
              {Object.entries(LIBRARY_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              aria-label="Folder"
              value={folderId}
              onChange={(event) => { setFolderId(event.target.value); setPage(0) }}
            >
              <option value="">Any folder</option>
              {folders.data?.map((folder) => (
                <option key={folder.id} value={folder.id}>{folder.name}</option>
              ))}
            </Select>
          </>
        }
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title="New library entry"
        description="A policy can be acknowledged; a document cannot. Choose the kind now — it decides what the entry can do."
        submitLabel="Save entry"
        loading={saveDocument.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveDocument.mutateAsync({
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  folderId: draft.folderId,
                  kind: draft.kind,
                  title: draft.title,
                  summary: draft.summary || null,
                  tags: draft.tags
                    ? draft.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
                    : [],
                  ownerEmployeeId: draft.ownerEmployeeId || null,
                  documentTypeId: draft.documentTypeId || null,
                  reviewDueOn: draft.reviewDueOn || null,
                  retentionRuleId: draft.retentionRuleId || null,
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That entry could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="library-folder">Folder</Label>
              <Select
                id="library-folder"
                className="w-full"
                value={draft.folderId}
                onChange={(event) => setDraft({ ...draft, folderId: event.target.value })}
              >
                <option value="">Choose a folder…</option>
                {folders.data?.map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="library-kind">Kind</Label>
              <Select
                id="library-kind"
                className="w-full"
                value={draft.kind}
                onChange={(event) =>
                  setDraft({ ...draft, kind: event.target.value as LibraryKind })}
              >
                <option value="POLICY">Policy — can be acknowledged</option>
                <option value="DOCUMENT">Document — cannot be acknowledged</option>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="library-title">Title</Label>
              <Input
                id="library-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="library-summary">What it is about</Label>
              <Input
                id="library-summary" value={draft.summary}
                onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="library-tags">Tags</Label>
              <Input
                id="library-tags" value={draft.tags}
                onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">Comma separated.</p>
            </div>
            <div>
              <Label htmlFor="library-owner">Owner</Label>
              <EmployeePicker
                  id="library-owner"
                  value={draft.ownerEmployeeId}
                  onChange={(employeeId) => setDraft({ ...draft, ownerEmployeeId: employeeId ?? '' })}
                />
              <p className="mt-1 text-xs text-slate-600">
                Told when it falls due for review, along with HR.
              </p>
            </div>
            <div>
              <Label htmlFor="library-review">Review due</Label>
              <DatePicker
                id="library-review" value={draft.reviewDueOn}
                onChange={(event) => setDraft({ ...draft, reviewDueOn: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Blank means nothing will ever remind anybody to look at this again.
              </p>
            </div>
            <div>
              <Label htmlFor="library-doctype">Alert schedule</Label>
              <Select
                id="library-doctype"
                className="w-full"
                value={draft.documentTypeId}
                onChange={(event) => setDraft({ ...draft, documentTypeId: event.target.value })}
              >
                <option value="">Warn once, on the day</option>
                {documentTypes.data?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Borrows a document type's schedule, so a review warns on the same rhythm as a
                visa. Without one there is no schedule to follow.
              </p>
            </div>
            <div>
              <Label htmlFor="library-retention">Retention rule</Label>
              <Select
                id="library-retention"
                className="w-full"
                value={draft.retentionRuleId}
                onChange={(event) => setDraft({ ...draft, retentionRuleId: event.target.value })}
              >
                <option value="">None — nothing says when this may be destroyed</option>
                {rules.data?.map((rule) => (
                  <option key={rule.id} value={rule.id}>{rule.name}</option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
